/**
 * PromoSection Component
 * 
 * Displays promotional content in a dedicated section on the homepage.
 * Fetches active promos with display_type='section'.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HiTag, HiGift, HiSparkles } from 'react-icons/hi';

interface Promo {
  id: string;
  title: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed' | 'info' | null;
  discount_value: number | null;
  promo_code: string | null;
  free_item: string | null;
  banner_image_url: string | null;
  is_daily_special: boolean;
  /** Site path such as /menu/thanksgiving-2026. Empty promos keep the call button only. */
  link_path?: string | null;
}

export default function PromoSection() {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/promos/active?display_type=section')
      .then((res) => res.json())
      .then((data) => {
        setPromos(data.promos || []);
      })
      .catch((err) => {
        console.error('Failed to fetch section promos:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Don't render if loading or no promos
  if (loading || promos.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-gradient-to-r from-purple-600 to-pink-500 text-white">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
            <HiSparkles className="w-5 h-5" />
            <span className="font-medium">Special Offers</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold">
            Don't Miss Out!
          </h2>
        </motion.div>

        <div className={`grid gap-6 ${promos.length === 1 ? 'max-w-md mx-auto' : promos.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'}`}>
          {promos.map((promo, index) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-4 sm:p-6 text-gray-900 shadow-xl"
            >
              {/* Image if available. Tall flyers and 1080×1350 menu photos stay fully visible. */}
              {promo.banner_image_url && (
                promo.link_path ? (
                  <Link
                    to={promo.link_path}
                    className="mb-4 -mx-2 sm:mx-0 flex justify-center overflow-hidden rounded-xl bg-purple-50"
                  >
                    <img
                      src={promo.banner_image_url}
                      alt={promo.title}
                      className="max-h-[70vh] w-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </Link>
                ) : (
                  <div className="mb-4 -mx-2 sm:mx-0 flex justify-center overflow-hidden rounded-xl bg-purple-50">
                    {/* object-contain keeps a tall flyer whole. object-cover crops the prices off. */}
                    <img
                      src={promo.banner_image_url}
                      alt={promo.title}
                      className="max-h-[70vh] w-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                )
              )}

              {/* Daily special badge */}
              {promo.is_daily_special && (
                <div className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
                  <HiTag className="w-4 h-4" />
                  TODAY ONLY
                </div>
              )}

              {/* Title */}
              <h3 className="text-xl font-bold mb-2">{promo.title}</h3>

              {/* Discount display */}
              {promo.discount_type === 'percentage' && promo.discount_value && (
                <div className="text-3xl font-extrabold text-purple-600 mb-2">
                  {promo.discount_value}% OFF
                </div>
              )}
              {promo.discount_type === 'fixed' && promo.discount_value && (
                <div className="text-3xl font-extrabold text-purple-600 mb-2">
                  ${promo.discount_value} OFF
                </div>
              )}

              {/* Description */}
              {promo.description && (
                <p className="text-gray-600 mb-4 whitespace-pre-line">{promo.description}</p>
              )}

              {promo.link_path ? (
                <Link
                  to={promo.link_path}
                  className="inline-flex items-center justify-center w-full bg-purple-600 text-white px-4 py-2.5 rounded-full font-bold hover:bg-purple-700 transition-colors"
                >
                  {/* Birthday cookie is in-store only — don't imply online checkout. */}
                  {/stuffed|birthday/i.test(`${promo.title} ${promo.link_path || ''}`)
                    ? 'See stuffed cookies'
                    : 'Order online'}
                </Link>
              ) : null}

              {/* Free item */}
              {promo.free_item && (
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg mb-4">
                  <HiGift className="w-5 h-5" />
                  <span className="font-medium">Free {promo.free_item}</span>
                </div>
              )}

              {/* Promo code */}
              {promo.promo_code && (
                <div className="bg-gray-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Use code:</p>
                  <p className="font-mono font-bold text-lg text-purple-600">
                    {promo.promo_code}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <a
            href="tel:7055034966"
            className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Call to Order: (705) 503-4966
          </a>
        </motion.div>
      </div>
    </section>
  );
}
